import { type AuthSession } from 'aws-amplify/auth';
import jwtDecode from 'jwt-decode';
import { configure, makeAutoObservable } from 'mobx';
import { createContext, useContext } from 'react';
import {
	string as zodString,
	object as zodObject,
	type infer as zodInfer,
	number as zodNumber,
	optional as zodOptional,
} from 'zod/mini';

import { AuthenticationError } from '../components/ErrorPage/Errors';
import { getOrCreateInitializedLogRocketModel } from '../components/LogRocket/LogRocketModel';
import { getNavinaAuth0AuthURL } from '../config/Environment';
import { getOrCreateInitializedFeatureFlag } from '../config/FeatureFlag';
import { UserView } from '../types';
import { getOrCreateAddonCommunicator, OverlayProtocolTypes } from '../utils/addon/addon-communicator';
import { getOrCreateAnalytics } from '../utils/analytics';
import { getOrCreateAppcues } from '../utils/appcues';
import { Auth, SignInStatus, type UserMetadata } from '../utils/auth';

import type { PermissionType } from '../components/user-permissions/NavinaFeature';

const DECODED_SSO_JWT_SCHEMA = zodObject({
	'https://navina.ai/email': zodString(),
	// 'https://navina.ai/dataSourceId': z.string(),
	// 'https://navina.ai/dataSourceIds': z.array(z.string()),
	// iss: z.url(),
	// aud: z.string(),
	sub: zodString(),
	// iat: z.number(),
	// exp: z.number(),
	// sid: z.string(),
});

type SsoJwt = zodInfer<typeof DECODED_SSO_JWT_SCHEMA>;

const DECODED_COGNITO_JWT_SCHEMA = zodObject({
	sub: zodString(),
	exp: zodNumber(),
	emrName: zodOptional(zodString()),
	isAdmin: zodOptional(zodString()),
	permissions: zodOptional(zodString()),
	analyticsMetadata: zodString(),
	permission: zodOptional(zodString()),
});

export type CognitoNavinaJwt = zodInfer<typeof DECODED_COGNITO_JWT_SCHEMA>;

const ANALYTICS_METADATA_JWT_KEY = 'analyticsMetadata';
const PERMISSIONS_METADATA_JWT_KEY = 'permissions';

const USER_VIEW_MAP = {
	Coder: UserView.Coder,
	Provider: UserView.Provider,
	Classic: UserView.Provider,
} as const satisfies Record<NonNullable<NavinaPermissions['view']>, UserView>;

configure({ enforceActions: 'always' });

type StringBoolean = 'true' | 'false';

export interface NavinaPermissions extends Partial<Record<PermissionType, StringBoolean>> {
	readonly view?: 'Coder' | 'Provider' | 'Classic';
}

interface AuthStoreValues {
	readonly email: string;
	readonly password: string;
}

class AuthStore {
	readonly auth: Auth;
	initializing = true;
	inProgress = false;
	loggedIn: boolean | undefined = undefined;
	token = '';
	admin = false;
	multipleProviders = false;
	status: SignInStatus = SignInStatus.initial;
	userFullName = '';
	permissions: NavinaPermissions = {};
	isSAML = false;

	values: AuthStoreValues = {
		email: '',
		password: '',
	};

	constructor() {
		makeAutoObservable(this);
		this.auth = new Auth();
	}

	readonly init = async (): Promise<void> => {
		await this.auth.getAuthData(this.afterInit);
	};

	readonly resetToken = (): void => {
		this.setToken('');
	};

	readonly setToken = (token: string): void => {
		this.token = token;
	};

	readonly setIsSAML = (isSAML: boolean): void => {
		this.isSAML = isSAML;
	};

	readonly setValues = (values: typeof this.values): void => {
		this.values = values;
	};

	readonly setEmail = (email: string): void => {
		this.setValues({ ...this.values, email });
	};

	readonly setPassword = (password: string): void => {
		this.setValues({ ...this.values, password });
	};

	readonly setSignInStatus = (value: SignInStatus): void => {
		this.status = value;
	};

	readonly setInProgress = (inProgress: boolean): void => {
		this.inProgress = inProgress;
	};

	readonly setIsInitializing = (isInitializing: boolean): void => {
		this.initializing = isInitializing;
	};

	readonly setIsLoggedIn = (isLoggedIn: boolean): void => {
		this.loggedIn = isLoggedIn;
	};

	readonly setIsAdmin = (isAdmin: boolean): void => {
		this.admin = isAdmin;
	};

	readonly setIsMultipleProviders = (multipleProviders: boolean): void => {
		this.multipleProviders = multipleProviders;
	};

	readonly setPermissions = (permissions: NavinaPermissions | string): void => {
		this.permissions = typeof permissions === 'string' ? JSON.parse(permissions) : {};
	};

	get signInStatus(): typeof this.status {
		return this.status;
	}

	get isInProgress(): typeof this.inProgress {
		return this.inProgress;
	}

	get isLoggedIn(): typeof this.loggedIn {
		return this.loggedIn;
	}

	get isInitializing(): typeof this.initializing {
		return this.initializing;
	}

	get getToken(): typeof this.token {
		return this.token;
	}

	get getIsSAML(): typeof this.isSAML {
		return this.isSAML;
	}

	get getEmail(): typeof this.values.email {
		return this.values.email;
	}

	get isMultipleProviders(): typeof this.multipleProviders {
		return this.multipleProviders;
	}

	get isAdmin(): typeof this.admin {
		return this.admin;
	}

	get getPermissions(): typeof this.permissions {
		return this.permissions;
	}

	get userView(): UserView {
		const view = this.getPermissions.view;

		if (!view) {
			throw new AuthenticationError({ description: 'User view not found' });
		}

		const userView: UserView = USER_VIEW_MAP[view];

		if (!userView) {
			throw new AuthenticationError({ description: `Unknown user view: ${view}` });
		}

		return userView;
	}

	readonly reset = (): void => {
		this.setEmail('');
		this.setPassword('');
		this.resetToken();
		this.resetAnalyticsMetadata();
		this.setIsLoggedIn(false);
		this.setIsAdmin(false);
		this.setSignInStatus(SignInStatus.initial);
		this.auth.signOut(this.isSAML);
		getOrCreateAnalytics().reset();
	};

	readonly thirdPartyAppsIdentifications = (
		navinaUserId: string,
		username: string,
		analyticsMetadata: Record<string, unknown>,
	): void => {
		// Mixpanel
		console.log('thirdPartyAppsIdentifications', navinaUserId, username, analyticsMetadata);
		if (username) {
			getOrCreateAnalytics().identify(navinaUserId, username);
		}
		getOrCreateAnalytics().register(analyticsMetadata);

		// LogRocket
		try {
			const traits: { [propName: string]: string | number | boolean } = Object.entries(analyticsMetadata)
				.filter(([_, value]): boolean => ['boolean', 'number', 'string'].includes(typeof value))
				.reduce((obj, [key, value]) => Object.assign(obj, { [key]: value }), {});

			getOrCreateInitializedLogRocketModel().identify(username || 'Unknown', traits || {});
		} catch (err) {
			console.error('Failed logrocket identification', {
				navinaUserId,
				username,
				analyticsMetadata,
				err,
				this: this,
			});
		}

		// Appcues
		try {
			getOrCreateAppcues().identify(navinaUserId, analyticsMetadata || {});
		} catch (err) {
			console.error('Failed Appcues identification', {
				navinaUserId,
				username,
				analyticsMetadata,
				err,
				this: this,
			});
		}

		// Datadog
		import('@datadog/browser-rum')
			.then(function initDataDogRum({ datadogRum }): void {
				datadogRum.setUser({
					id: username || 'Unknown',
					clinic: analyticsMetadata?.clinic || 'Unknown',
					emrName: analyticsMetadata?.emrName || 'Unknown',
				});
			})
			.catch((err): void => console.error('Failed Datadog identification', err));
	};

	private readonly parseJwtMetadata = (
		jwt: Partial<CognitoNavinaJwt>,
	): {
		analyticsMetadata: Record<string, unknown>;
		permissionsMetadata: Record<string, unknown>;
	} => {
		const analyticsMetadata = this.tryParseJsonFromJwt(jwt, ANALYTICS_METADATA_JWT_KEY, 'analytics metadata');
		const permissionsMetadata = this.tryParseJsonFromJwt(jwt, PERMISSIONS_METADATA_JWT_KEY, 'permissions metadata');
		return { analyticsMetadata, permissionsMetadata };
	};

	private readonly tryParseJsonFromJwt = (
		jwt: Partial<CognitoNavinaJwt>,
		key: keyof CognitoNavinaJwt,
		errorContext: string,
	): Record<string, unknown> => {
		try {
			const metadataString = jwt[key];
			return metadataString && typeof metadataString === 'string' ? JSON.parse(metadataString) : {};
		} catch (err) {
			console.warn(`${errorContext} corrupted`, { error: err, this: this });
			return {};
		}
	};

	private readonly handleSuccessfulLogin = (username: string | null, session: AuthSession | null): void => {
		const analytics = getOrCreateAnalytics();
		analytics.track(analytics.idsNames.LogInSuccessful, { username, status: SignInStatus.success });

		const jwt = this.extractJwtFromSession(session);
		const { analyticsMetadata, permissionsMetadata } = this.parseJwtMetadata(jwt);
		const resolvedUsername = this.resolveUsername(username);
		const emrName = this.extractEmrName(jwt);
		const { sub } = jwt;

		this.setupUserSession(session, jwt);
		this.setupUserIdentifications(resolvedUsername, sub, analyticsMetadata, permissionsMetadata, emrName);
		this.setupFeatureFlags(sub, resolvedUsername, analyticsMetadata, permissionsMetadata, emrName);
		this.setupUserProfile(resolvedUsername, analyticsMetadata, jwt);
	};

	private readonly extractJwtFromSession = (session: AuthSession | null): Partial<CognitoNavinaJwt> => {
		return session?.tokens?.idToken ? jwtDecode<CognitoNavinaJwt>(session.tokens.idToken.toString()) : {};
	};

	private readonly resolveUsername = (username: string | null): string | null => {
		return username || this.getEmail || null;
	};

	private readonly extractEmrName = (jwt: Partial<CognitoNavinaJwt>): string => {
		return 'emrName' in jwt ? jwt.emrName || 'unknown' : 'unknown';
	};

	private readonly setupUserSession = (session: AuthSession | null, jwt: Partial<CognitoNavinaJwt>): void => {
		const maybeIdJwtToken = session?.tokens?.idToken?.toString();
		if (maybeIdJwtToken) {
			const idJwtToken = maybeIdJwtToken;
			this.setToken(idJwtToken);

			const invalidatedDecodedIdToken = jwtDecode(idJwtToken);
			const decodedIdToken = DECODED_COGNITO_JWT_SCHEMA.parse(invalidatedDecodedIdToken);
			this.setAnalyticsMetadata(JSON.parse(decodedIdToken.analyticsMetadata));

			this.setIsLoggedIn(true);

			const isAdmin = jwt.isAdmin === 'true';
			this.setIsAdmin(isAdmin);
		}
	};

	private readonly setupUserIdentifications = (
		username: string | null,
		sub: string | undefined,
		analyticsMetadata: Record<string, unknown>,
		permissionsMetadata: Record<string, unknown>,
		emrName: string,
	): void => {
		if (username && sub) {
			this.setEmail(username);
			this.thirdPartyAppsIdentifications(sub, username, {
				...permissionsMetadata,
				...analyticsMetadata,
				emrName,
			});
		}
	};

	private readonly setupFeatureFlags = (
		sub: string | undefined,
		username: string | null,
		analyticsMetadata: Record<string, unknown>,
		permissionsMetadata: Record<string, unknown>,
		emrName: string,
	): void => {
		if (sub && username) {
			getOrCreateInitializedFeatureFlag({
				id: sub,
				email: username,
				custom: {
					...analyticsMetadata,
					...permissionsMetadata,
					emr_name: emrName,
					is_admin: false,
				},
			});
		}
	};

	private readonly setupUserProfile = (
		username: string | null,
		analyticsMetadata: Record<string, unknown>,
		jwt: Partial<CognitoNavinaJwt>,
	): void => {
		if (username) {
			this.setFullName(analyticsMetadata, username);
		}
		if (jwt.permissions) {
			this.setPermissions(jwt.permissions);
		}
	};

	private readonly handleFailedLogin = (): void => {
		this.setIsLoggedIn(false);
		this.resetToken();
		this.resetAnalyticsMetadata();
	};

	readonly afterLogin = (username: string | null, session: AuthSession | null, status: SignInStatus): void => {
		console.log('afterLogin called', username, session, status);
		this.setSignInStatus(status);

		if (status === SignInStatus.success || status === SignInStatus.totpRequired) {
			this.handleSuccessfulLogin(username, session);
		} else {
			this.handleFailedLogin();
		}
		this.setInProgress(false);
	};

	readonly afterInit = (
		username: string | null,
		session: AuthSession | null,
		auth0Token: string,
		userMetadata: UserMetadata | null,
		isSSO = false,
	): void => {
		console.log(`afterInit called username='${username}'`, session);

		if (username && session) {
			this.handleCognitoLogin(username, session);
		} else if (username && auth0Token && userMetadata) {
			this.handleSSOLogin(username, auth0Token, userMetadata);
		} else {
			this.handleFailedInit(username, session);
		}

		if (!isSSO) {
			this.setIsInitializing(false);
			this.setInProgress(false);
		}
	};

	private readonly handleCognitoLogin = (username: string, session: AuthSession): void => {
		console.log('handle cognito login');
		const maybeIdJwtToken = session.tokens?.idToken?.toString();
		const maybeDecodedCognitoJwt = maybeIdJwtToken ? jwtDecode(maybeIdJwtToken) : null;

		const jwt = DECODED_COGNITO_JWT_SCHEMA.parse(maybeDecodedCognitoJwt);

		const { analyticsMetadata, permissionsMetadata } = this.parseJwtMetadata(jwt);

		this.setBasicAuthState(username, session.tokens?.idToken?.toString() || '', false);
		this.setUserPermissions(jwt);
		this.setAnalyticsMetadata(analyticsMetadata);
		this.setupThirdPartyIntegrations(jwt, username, analyticsMetadata, permissionsMetadata);
		this.trackAuthInit('Cognito', username);
	};

	private readonly handleSSOLogin = (_username: string, auth0Token: string, userMetadata: UserMetadata): void => {
		console.log('handle sso login');
		const invalidatedDecodedIdToken = jwtDecode(auth0Token);
		console.log('Auth0 JWT Payload:', invalidatedDecodedIdToken);

		const decodedIdToken = DECODED_SSO_JWT_SCHEMA.parse(invalidatedDecodedIdToken);

		const { sub: subId, 'https://navina.ai/email': ssoEmail } = decodedIdToken;
		const { navinaUsername, analyticsMetadata } = userMetadata;

		this.setAnalyticsMetadata(analyticsMetadata);
		this.setBasicAuthState(navinaUsername, auth0Token, true);
		this.setSSOPermissions(userMetadata);
		this.setupSSOThirdPartyIntegrations(subId, navinaUsername, userMetadata, ssoEmail);
		this.trackAuthInit('SSO', ssoEmail);
		this.notifySSOSuccess();
		this.setIsInitializing(false);
		this.setInProgress(false);
	};

	private readonly handleFailedInit = (username: string | null, session: AuthSession | null): void => {
		console.warn('AfterInit failed, setting isLoggedIn to false', username, session);
		this.setIsLoggedIn(false);
		this.resetToken();
		this.resetAnalyticsMetadata();
	};

	private readonly setBasicAuthState = (email: string, token: string, isSAML: boolean): void => {
		this.setEmail(email);
		this.setToken(token);
		this.setIsLoggedIn(true);
		this.setIsSAML(isSAML);
	};

	private readonly setUserPermissions = (jwt: CognitoNavinaJwt): void => {
		// @ts-expect-error - Legacy code
		const groups: readonly string[] = jwt['cognito:groups'] || [];
		const isAdmin = jwt.isAdmin === 'true';

		this.setIsAdmin(isAdmin);
		this.setIsMultipleProviders(groups && groups.includes('MultipleProviders'));
		this.setPermissions(jwt.permissions || '');
	};

	private readonly setSSOPermissions = (userMetadata: UserMetadata): void => {
		this.setIsAdmin(userMetadata.isAdmin);
		this.setIsMultipleProviders(false);
		this.setPermissions(JSON.stringify(userMetadata.permissions));
	};

	private readonly setupThirdPartyIntegrations = (
		jwt: CognitoNavinaJwt,
		username: string,
		analyticsMetadata: Record<string, unknown>,
		permissionsMetadata: Record<string, unknown>,
	): void => {
		const emrName = 'emrName' in jwt ? jwt.emrName : 'unknown';
		const isAdmin = jwt.isAdmin === 'true';

		if (jwt.sub && username) {
			this.thirdPartyAppsIdentifications(jwt.sub, username, {
				...permissionsMetadata,
				...analyticsMetadata,
				emrName,
			});

			getOrCreateInitializedFeatureFlag({
				id: jwt.sub,
				email: username,
				custom: {
					...analyticsMetadata,
					...permissionsMetadata,
					emr_name: emrName,
					is_admin: isAdmin,
				},
			});

			this.setFullName(analyticsMetadata, username);
		}
	};

	private readonly setupSSOThirdPartyIntegrations = (
		subId: string,
		navinaUsername: string,
		userMetadata: UserMetadata,
		ssoEmail: string,
	): void => {
		this.thirdPartyAppsIdentifications(subId, navinaUsername, {
			...userMetadata.permissions,
			...userMetadata.analyticsMetadata,
			emrName: userMetadata.emrName,
		});

		getOrCreateInitializedFeatureFlag({
			id: subId,
			email: navinaUsername,
			custom: {
				...userMetadata.analyticsMetadata,
				...userMetadata.permissions,
				emr_name: userMetadata.emrName,
				is_admin: userMetadata.isAdmin,
				sso_email: ssoEmail,
			},
		});

		this.setFullName({}, navinaUsername);
	};

	private readonly trackAuthInit = (authType: string, username: string): void => {
		const analytics = getOrCreateAnalytics();
		analytics.track(analytics.idsNames.AuthInitDone, {
			username_attempt: username,
			auth_init_type: authType,
		});
	};

	private readonly notifySSOSuccess = (): void => {
		const addonCommunicator = getOrCreateAddonCommunicator();
		addonCommunicator.sendToAddon(OverlayProtocolTypes['addon-sso-login-succeed'], {});
		addonCommunicator.sendToAddon(OverlayProtocolTypes['addon-sso-login-completed'], {});
	};

	readonly setFullName = (analyticsMetadata: any, username: string): void => {
		let userFullName = username;
		if ('first_name' in analyticsMetadata && 'last_name' in analyticsMetadata) {
			userFullName = `${analyticsMetadata.first_name} ${analyticsMetadata.last_name}`;
		}

		this.userFullName = userFullName;
	};

	readonly login = async (
		verificationCode?: string | null,
		callback?: (status: SignInStatus) => void,
	): Promise<void> => {
		const analytics = getOrCreateAnalytics();

		analytics.track(analytics.idsNames.LogInAttempt, { username_attempt: this.values.email });

		this.setInProgress(true);

		await this.auth.signIn(
			this.values.email,
			this.values.password,
			(email: string | null, session: AuthSession | null, status: SignInStatus): void => {
				this.afterLogin(email, session, status);
				callback?.(status);
			},
			verificationCode || undefined,
		);
	};

	readonly sendMFA = async (verificationCode: string): Promise<void> => {
		this.setInProgress(true);
		await this.auth.sendMFA(verificationCode, this.afterLogin.bind(this));
	};

	readonly refreshToken = async (): Promise<AuthSession | null> => {
		console.log('user token refresh');
		if (this.getIsSAML && this.token) {
			const newUri = getNavinaAuth0AuthURL();
			this.resetToken();
			this.resetAnalyticsMetadata();
			globalThis.location.href = newUri;
			return null;
		}

		try {
			const session = await this.auth.refreshToken();
			const maybeIdJwtToken = session.tokens?.idToken?.toString();
			const invalidatedDecodedIdToken = maybeIdJwtToken ? jwtDecode(maybeIdJwtToken) : null;
			const decodedIdToken = DECODED_COGNITO_JWT_SCHEMA.parse(invalidatedDecodedIdToken);
			this.setToken(maybeIdJwtToken || '');
			this.setAnalyticsMetadata(JSON.parse(decodedIdToken.analyticsMetadata));
			return session;
		} catch (err) {
			this.resetToken();
			this.resetAnalyticsMetadata();
			throw err;
		}
	};

	readonly getRefreshTokenFromLocalStorage = async () => {
		return this.auth.getRefreshTokenFromCookie();
	};

	readonly logoutAndRedirectToLoginPage = async (): Promise<void> => {
		getOrCreateAnalytics().track(getOrCreateAnalytics().idsNames.LogOut);
		console.log('user logout');
		this.reset();
		await this.auth.signOut(this.isSAML);
		globalThis.location.href = globalThis.location.origin;
	};

	readonly changePassword = async (
		oldPassword: string,
		newPassword: string,
		callback: (err: any, result: any) => void,
	): Promise<void> => {
		await this.auth.changePassword(oldPassword, newPassword, callback);
	};

	// Password init on first login, equivalent for Sign Up
	readonly completeNewPasswordChallenge = async (newPassword: string): Promise<void> => {
		this.setInProgress(true);
		try {
			await this.auth.completeNewPasswordChallenge(newPassword, (): void => {
				this.setSignInStatus(SignInStatus.initial);
				this.setInProgress(false);
			});
		} catch (err) {
			this.setInProgress(false);
			this.setSignInStatus(SignInStatus.failure);
			throw err;
		}
	};

	readonly confirmForgotPassword = async (
		username: string,
		newPassword: string,
		emailCode: string,
		onSuccess: (succeed: boolean) => void,
		onFailure: (error: Error) => void,
	): Promise<void> => {
		await this.auth.confirmForgotPassword(username, newPassword, emailCode, onSuccess, onFailure);
	};

	readonly requestPasswordReset = async (username: string): ReturnType<typeof this.auth.requestPasswordReset> => {
		console.log('sending reset request - requestPasswordReset');
		this.setInProgress(true);
		try {
			const response = await this.auth.requestPasswordReset(username);
			return response;
		} catch (err) {
			console.error('error on requestPasswordReset', err);
			throw err;
		} finally {
			this.setInProgress(false);
		}
	};

	get jwtData(): CognitoNavinaJwt | SsoJwt | null {
		const { token } = this;
		if (!token) {
			return null;
		}

		const invalidatedDecodedIdToken = jwtDecode(token);

		const cognitoParseResult = DECODED_COGNITO_JWT_SCHEMA.safeParse(invalidatedDecodedIdToken);
		if (cognitoParseResult.success) {
			return cognitoParseResult.data;
		}

		const ssoParseResult = DECODED_SSO_JWT_SCHEMA.safeParse(invalidatedDecodedIdToken);
		if (ssoParseResult.success) {
			return ssoParseResult.data;
		}

		return null;
	}

	get navinaUserId(): string {
		const { jwtData } = this;

		if (!jwtData) {
			throw new AuthenticationError({ description: 'No JWT data', isHiddenError: true });
		}

		return jwtData.sub;
	}

	maybeAnalyticsMetadata: Record<string, unknown> | null = null;

	readonly setAnalyticsMetadata = (analyticsMetadata: typeof this.maybeAnalyticsMetadata): void => {
		this.maybeAnalyticsMetadata = analyticsMetadata;
	};

	readonly resetAnalyticsMetadata = (): void => {
		this.setAnalyticsMetadata(null);
	};

	get analyticsMetadata(): Record<string, unknown> {
		const { maybeAnalyticsMetadata } = this;
		if (!maybeAnalyticsMetadata) {
			throw new AuthenticationError({ description: 'No analytics metadata', isHiddenError: true });
		}

		return maybeAnalyticsMetadata;
	}
}

export const AuthStoreContext = createContext<AuthStore | null>(null);
export const useAuthStore = (): AuthStore => {
	const authStore = useContext(AuthStoreContext);
	if (!authStore) {
		throw new AuthenticationError({
			description: 'useAuthStore must be used within an AuthStoreProvider',
		});
	}
	return authStore;
};

// 'authStore.init()' used to be part of the AuthStore constructor. This was causing issues with the lazy initialization of the store.
// 'if (authStore === null) {' suffered from race conditions, where the store was not yet initialized, but the check was already done.
// So more and more stores were created.
// In order to speed up the occupation of 'let authStore: AuthStore | null = null', I separated the initialization from the constructor,
// which seems to have solved the problem!
// Yet, for safety, I also added this eager initialization here.
// TODO: Consider removing this eager initialization, as it is not necessary anymore. Only courage is needed. @AvivNavina
//
// let authStore: AuthStore | null = null;
//
let authStore: AuthStore | null = createAuthStore();
try {
	await authStore.init();
} catch (error) {
	console.error(error);
}

function createAuthStore(): AuthStore {
	return new AuthStore();
}

export type { AuthStore };

export function getOrCreateAuthStore(): AuthStore {
	if (authStore === null) {
		authStore = createAuthStore();
		authStore.init().catch(console.error);
	}
	return authStore;
}
